import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import { Footprints, HeartPulse, Repeat, ShieldCheck, MapPin, Users } from 'lucide-react';

const AmbulatoryTransportation = () => {
  const seo = {
    title: 'Ambulatory Medical Transportation Houston | MoveraCare Mobility',
    description: 'Comfortable ambulatory non-emergency medical transportation across Greater Houston for medical appointments, follow-ups, therapy, and outpatient care.',
    path: '/services/ambulatory-transportation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Ambulatory Medical Transportation',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: { '@type': 'MedicalBusiness', name: 'MoveraCare Mobility', telephone: '+1-832-925-2128', url: 'https://www.moveracaremobility.com' },
    },
  };

  return (
    <ServicePageTemplate
      testid="service-ambulatory"
      seo={seo}
      eyebrow="AMBULATORY TRANSPORTATION"
      heading="Ambulatory Medical Transportation for Every Appointment"
      intro="MoveraCare Mobility provides professional non-emergency transportation for medically stable ambulatory passengers — those who can walk independently or with limited assistance — across Greater Houston."
      who={{
        title: 'For Patients Who Walk Independently or With Limited Assistance',
        bullets: [
          'Patients traveling to medical, therapy, or diagnostic appointments',
          'Seniors and caregivers arranging safe transportation',
          'Post-surgery follow-up transportation',
          'Workers’ Compensation ambulatory transportation for injured workers',
          'Case managers coordinating outpatient trips',
          'Hospital and clinic discharge planners',
        ],
      }}
      included={{
        title: 'Ambulatory Service Includes',
        bullets: [
          'Comfortable sedan or van transport with a professional driver',
          'Medical, dental, and specialty appointment transportation',
          'Physical therapy and rehabilitation trips',
          'Diagnostic imaging (MRI, CT, X-Ray) transportation',
          'Occupational medicine and follow-up visits',
          'One-way, round-trip, and wait-and-return options',
          'Recurring scheduling for treatment programs',
        ],
      }}
      howItWorks={{
        title: 'Simple, Reliable Booking',
        steps: [
          { title: 'Trip Request', desc: 'Submit patient name, pickup, destination, appointment date and time, and any special notes.' },
          { title: 'Confirmation', desc: 'MoveraCare confirms the assigned vehicle, pickup window, and communicates the plan back to the requester.' },
          { title: 'Pickup', desc: 'Trained driver arrives on schedule and provides courteous, professional assistance.' },
          { title: 'Delivery & Return', desc: 'Passenger is transported to the appointment and returned when a round trip is scheduled.' },
        ],
      }}
      why={{
        title: 'Why Ambulatory Passengers Choose MoveraCare',
        items: [
          { icon: Footprints, title: 'Comfortable Vehicles', desc: 'Clean, professional vehicles for a smooth ride.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport.' },
          { icon: Repeat, title: 'Recurring Scheduling', desc: 'Standing schedules for therapy, follow-up, and treatment programs.' },
          { icon: HeartPulse, title: 'Healthcare-Focused', desc: 'Operations designed around medical appointments.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious', desc: 'Professional handling of passenger information.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Service across our verified Greater Houston area.' },
        ],
      }}
      faqs={[
        { question: 'What is the difference between ambulatory and wheelchair transportation?', answer: 'Ambulatory transportation is for passengers who can walk to and enter the vehicle independently or with limited assistance. Wheelchair transportation uses a wheelchair-accessible vehicle for passengers who remain seated in their wheelchair.' },
        { question: 'Can family members ride along?', answer: 'A family member or authorized companion may ride subject to seating and vehicle capacity. Please mention companions when scheduling.' },
        { question: 'Do you offer same-day ambulatory service?', answer: 'Same-day service may be available based on driver and vehicle availability. Call dispatch for urgent requests.' },
        { question: 'Do you provide round-trip ambulatory service?', answer: 'Yes. Round-trip and wait-and-return options are available with advance scheduling.' },
      ]}
    />
  );
};

export default AmbulatoryTransportation;
